import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Stack } from "@mui/material";
import ExecutiveInsightsDashboard from "../components/Dashboard/ExecutiveInsightsDashboard";
import {
  DEFAULT_FILTER_STATE,
} from "../components/Dashboard/dashboardConstants";
import {
  getAlerts,
  getDashboardFilterOptions,
  getDetailedPerformance,
  getDimensionExplorerData,
  getExecutiveOverview,
  getFlavourInsights,
  getMarketContext,
  getPlatformPerformance,
  getReviewInsights,
  getSentimentAnalysis,
  getSocialPerformance,
  getTopicInsights,
} from "../services/dashboardApiService";
import { convertToCSV, downloadCSV } from "../utils/csvUtils";

const createLoadState = () => ({
  executive: false,
  social: false,
  platform: false,
  sentiment: false,
  flavour: false,
  topics: false,
  reviews: false,
  alerts: false,
  market: false,
  detailed: false,
  explorer: false,
});

const createErrorState = () => ({
  executive: "",
  social: "",
  platform: "",
  sentiment: "",
  flavour: "",
  topics: "",
  reviews: "",
  alerts: "",
  market: "",
  detailed: "",
  explorer: "",
});

const DashboardPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [filterOptions, setFilterOptions] = useState({});
  const [filters, setFilters] = useState(DEFAULT_FILTER_STATE);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTER_STATE);
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(createLoadState());
  const [error, setError] = useState(createErrorState());
  const [drillPath, setDrillPath] = useState([]);
  const [topLevelError, setTopLevelError] = useState("");
  const [dimensionExplorerConfig, setDimensionExplorerConfig] = useState({
    dimension: "Platform",
    metric: "Engagements",
    chartType: "bar",
  });

  const loadFilterOptions = useCallback(async () => {
    const response = await getDashboardFilterOptions();
    if (!response || response.error) {
      setTopLevelError("Could not load filter options. Showing defaults.");
      return;
    }
    setFilterOptions(response);
  }, []);

  const runSectionLoad = useCallback(async (sectionKey, loader) => {
    setLoading((prev) => ({ ...prev, [sectionKey]: true }));
    setError((prev) => ({ ...prev, [sectionKey]: "" }));

    try {
      const result = await loader();
      setDashboardData((prev) => ({ ...prev, [sectionKey]: result || {} }));
    } catch (err) {
      setError((prev) => ({
        ...prev,
        [sectionKey]: err?.message || "Failed to load section data.",
      }));
    } finally {
      setLoading((prev) => ({ ...prev, [sectionKey]: false }));
    }
  }, []);

  const loadAll = useCallback(async (requestFilters, explorerConfig) => {
    await Promise.all([
      runSectionLoad("executive", () => getExecutiveOverview(requestFilters)),
      runSectionLoad("social", () => getSocialPerformance(requestFilters)),
      runSectionLoad("platform", () => getPlatformPerformance(requestFilters)),
      runSectionLoad("sentiment", () => getSentimentAnalysis(requestFilters)),
      runSectionLoad("flavour", () => getFlavourInsights(requestFilters)),
      runSectionLoad("topics", () => getTopicInsights(requestFilters)),
      runSectionLoad("reviews", () => getReviewInsights(requestFilters)),
      runSectionLoad("alerts", () => getAlerts(requestFilters)),
      runSectionLoad("market", () => getMarketContext(requestFilters)),
      runSectionLoad("detailed", () => getDetailedPerformance(requestFilters)),
      runSectionLoad("explorer", () => getDimensionExplorerData({ ...requestFilters, ...explorerConfig })),
    ]);
  }, [runSectionLoad]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadAll(appliedFilters, dimensionExplorerConfig);
    }, 300);
    return () => clearTimeout(timer);
  }, [appliedFilters, dimensionExplorerConfig, loadAll]);

  const isGlobalLoading = useMemo(() => Object.values(loading).some(Boolean), [loading]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTER_STATE);
    setAppliedFilters(DEFAULT_FILTER_STATE);
    setDrillPath([]);
  };

  const handleDrillDown = (dimension, value) => {
    if (!value) return;

    setDrillPath((prev) => [...prev, { dimension, value }]);

    setAppliedFilters((prev) => {
      const next = { ...prev };
      if (dimension === "platform") next.platform = [value];
      if (dimension === "flavour") next.flavour = [value];
      if (dimension === "topic") next.topicKeyword = [value];
      if (dimension === "sentiment") next.sentiment = [value];
      return next;
    });
  };

  const handleDrillBack = () => {
    setDrillPath((prev) => prev.slice(0, -1));
  };

  const handleExport = (name, rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const csv = convertToCSV(rows);
    downloadCSV(csv, `${name}_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const handleDimensionExplorerConfigChange = (key, value) => {
    setDimensionExplorerConfig((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {topLevelError && (
        <Stack sx={{ px: 2, pt: 2 }}>
          <Alert severity="warning">{topLevelError}</Alert>
        </Stack>
      )}

      <ExecutiveInsightsDashboard
        activeTab={activeTab}
        onTabChange={setActiveTab}
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
        dashboardData={dashboardData}
        loading={loading}
        error={error}
        drillPath={drillPath}
        onDrillDown={handleDrillDown}
        onDrillBack={handleDrillBack}
        onExport={handleExport}
        dimensionExplorerConfig={dimensionExplorerConfig}
        onDimensionExplorerConfigChange={handleDimensionExplorerConfigChange}
        isGlobalLoading={isGlobalLoading}
      />
    </Box>
  );
};

export default DashboardPage;
